import { defineStore } from 'pinia'
import { z } from 'zod'

import { useAuthStore } from './authStore'
import type { AuthUser } from './api'
import { clearStoredAuth, readStoredAuth, writeStoredAuth, type StoredAuth } from './session'
import { clientConfig } from '@/app/branding'

const payloadSchema = z.object({
  token: z.string().min(1),
  refresh: z.string().min(1).optional(),
  user: z.record(z.unknown()).optional(),
})

const impersonatedUserKey = 'impersonated_user'
const originalSessionKey = `papaproxy.auth.original.${clientConfig.id}`

export const useImpersonationStore = defineStore('impersonation', {
  state: () => ({ user: null as AuthUser | null }),
  getters: { active: (state) => Boolean(state.user) },
  actions: {
    boot() {
      const raw = window.localStorage.getItem(impersonatedUserKey)
      if (!raw) return
      try {
        this.user = JSON.parse(raw) as AuthUser
      } catch {
        window.localStorage.removeItem(impersonatedUserKey)
      }
    },
    async start(rawPayload: string) {
      const payload = payloadSchema.parse(JSON.parse(decodeURIComponent(rawPayload)))
      const original = readStoredAuth()
      if (original) window.localStorage.setItem(originalSessionKey, JSON.stringify(original))

      const auth = useAuthStore()
      clearStoredAuth()
      writeStoredAuth({
        accessToken: payload.token,
        ...(payload.refresh ? { refreshToken: payload.refresh } : {}),
        remember: true,
      })
      this.user = (payload.user as AuthUser | undefined) ?? null
      if (this.user) window.localStorage.setItem(impersonatedUserKey, JSON.stringify(this.user))
      auth.setToken(payload.token, payload.refresh)
      if (this.user) auth.setUser(this.user)
      return auth.fetchProfile()
    },
    async stop() {
      const auth = useAuthStore()
      const rawOriginal = window.localStorage.getItem(originalSessionKey)
      clearStoredAuth()
      window.localStorage.removeItem(originalSessionKey)
      window.localStorage.removeItem(impersonatedUserKey)
      this.user = null
      if (!rawOriginal) {
        auth.logout()
        return null
      }
      const original = JSON.parse(rawOriginal) as StoredAuth
      writeStoredAuth(original)
      auth.setToken(original.accessToken, original.refreshToken, original.remember)
      return auth.fetchProfile()
    },
  },
})
